import { useGetProviders } from '@/api/get-providers';
import { Button } from '@/components/button';
import { useTranslation } from '@/contexts/i18n-context';
import { ProviderListCard } from '@/components/encounter/provider/provider-list-card';
import { SelectProviderButton } from '@/components/encounter/provider/select-provider-button';
import { useEncounter } from '@/contexts/encounter-context';
import { Section } from '@/components/containers/section';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Fragment } from 'react/jsx-runtime';
import styles from './provider-list.module.css';
import { Form } from '@/components/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DropdownInput } from '@/components/inputs/dropdown-input';
import { CheckboxGroup } from '@/components/inputs/checkbox-group';
import { RadioGroup } from '@/components/inputs/radio-group';
import { Sheet } from '@/components/containers/sheet';

import clsx from 'clsx';
import { z } from 'zod';
import {
  Gender,
  GetProvidersErrorType,
  GetProvidersParams,
  SortByNameDirection,
} from '@/api/api.schemas';
import { useState } from 'react';
import { Loader } from '@/components/loaders/loader';
import { EmptyList } from '@/components/containers/empty-list';
import { Separator } from '@/components/separator';
import { EmptyListWrapper } from '@/components/containers/empty-list-wrapper';

const schema = z.object({
  encounterId: z.number(),
  genders: z.array(z.nativeEnum(Gender)).optional(),
  sort: z.nativeEnum(SortByNameDirection).optional(),
});

type FormValues = z.infer<typeof schema>;

const translationBase = 'visit.providerSelection';

export function ProviderList() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();

  const isMobile = useMediaQuery('--screen-md');

  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      encounterId: encounter.id,
      genders: undefined,
      sort: SortByNameDirection.None,
    },
  });

  const [mobileFormValues, setMobileFormValues] = useState<GetProvidersParams>(form.getValues());

  const formValues = form.watch();

  const { data, isFetching, error } = useGetProviders({
    encounterId: formValues.encounterId,
    genders: isMobile ? mobileFormValues.genders : formValues.genders,
    sort: formValues.sort,
  });

  const onSubmit = (data: FormValues) => {
    setMobileFormValues(data);
  };

  const isListEmpty = !isFetching && data?.providers.length === 0;
  const isFetchingOrEmpty = isFetching || isListEmpty;

  const onClear = () => {
    form.reset();
    form.setValue('sort', mobileFormValues.sort);
    setMobileFormValues((prev) => ({
      ...prev,
      genders: undefined,
    }));
  };

  const hasDesktopSelectedFilters = formValues.genders && formValues.genders.length > 0;
  const hasMobileSelectedFilters = mobileFormValues.genders && mobileFormValues.genders.length > 0;

  if (error?.type === GetProvidersErrorType.NotFound) {
    return (
      <Section>
        <EmptyListWrapper title={t(`${translationBase}.noProviders`)} />
      </Section>
    );
  }

  return (
    <Section className={styles.form}>
      <div className={styles.topBar}>
        {!encounter.isMedicaid && (
          <div className={styles.assignContainer}>
            <span className={styles.description}>
              {t(`${translationBase}.assignProvider.description`, {
                buttonText: (
                  <span key="assign-provider-button" className="bold">
                    {t(`${translationBase}.assignProvider.button`)}
                  </span>
                ),
              })}
            </span>
            <SelectProviderButton>
              {t(`${translationBase}.assignProvider.button`)}
            </SelectProviderButton>
          </div>
        )}
        {isMobile ? (
          <div className={styles.filterButtons}>
            <Sheet
              isOpen={isFilterSheetOpen}
              onClose={() => setIsFilterSheetOpen(false)}
              title={t(`${translationBase}.filters.sheetTitle`)}
              {...(hasMobileSelectedFilters ? { onClear } : {})}
            >
              <Form form={form} onSubmit={onSubmit}>
                <span className={clsx('body-3', styles.optionTitle)}>
                  {t(`${translationBase}.filters.gender`)}
                </span>
                <div className={styles.divider} />
                <GenderCheckboxGroup />
                <Button
                  onClick={() => {
                    setMobileFormValues(form.getValues());
                    setIsFilterSheetOpen(false);
                  }}
                  className={styles.applyFilterButton}
                  fullWidth
                >
                  {t(`${translationBase}.filters.apply`)}
                </Button>
              </Form>
            </Sheet>
            <Sheet
              isOpen={isSortSheetOpen}
              onClose={() => setIsSortSheetOpen(false)}
              title={t(`${translationBase}.sort.sheetTitle`)}
            >
              <Form form={form} onSubmit={onSubmit}>
                <SortRadioGroup />
              </Form>
            </Sheet>
            <Button
              onClick={() => setIsFilterSheetOpen(true)}
              variant="filled"
              leftIcon="filter_list"
              className={styles.filterButton}
            >
              {t(`${translationBase}.filters.sheetTitle`)}
            </Button>
            {!isFetchingOrEmpty && (
              <Button
                onClick={() => setIsSortSheetOpen(true)}
                variant="filled"
                leftIcon="swap_vert"
                className={styles.filterButton}
              >
                {t(`${translationBase}.sort.sheetTitle`)}
              </Button>
            )}
          </div>
        ) : (
          <Form form={form} onSubmit={onSubmit} className={styles.filterBar}>
            <div className={styles.filterOptions}>
              <span>{t(`${translationBase}.filters.title`)}</span>
              <DropdownInput
                iconProps={
                  hasDesktopSelectedFilters
                    ? { name: 'check', className: styles.filterIcon }
                    : undefined
                }
                text={t(`${translationBase}.filters.gender`)}
              >
                <GenderCheckboxGroup />
              </DropdownInput>

              {hasDesktopSelectedFilters && (
                <Button variant="textOnly" onClick={onClear} className={styles.clearButton}>
                  {t('common.clearAll')}
                </Button>
              )}
            </div>
            {!isFetchingOrEmpty && (
              <div className={clsx(styles.sortOptions, 'body-3')} data-testid="provider-sort">
                <span>{t(`${translationBase}.sort.title`)}</span>
                <DropdownInput
                  text={t(
                    `${translationBase}.sort.options.${formValues.sort ?? SortByNameDirection.None}`,
                  )}
                  position="right"
                  className={styles.sortButton}
                >
                  <SortRadioGroup />
                </DropdownInput>
              </div>
            )}
          </Form>
        )}
      </div>
      {isFetching ? (
        <Loader className={styles.loader} type="small" />
      ) : (
        <>
          {isListEmpty && <EmptyList title={`${translationBase}.emptyState.title`} />}
          {data?.providers?.map((provider, index) => {
            return (
              <Fragment key={provider.id}>
                <ProviderListCard provider={provider} />
                {index !== data.providers.length - 1 && <Separator />}
              </Fragment>
            );
          })}
        </>
      )}
    </Section>
  );
}

function GenderCheckboxGroup() {
  const { t } = useTranslation();

  return (
    <CheckboxGroup
      label={t(`${translationBase}.filters.gender`)}
      readersOnlyLabel
      name="genders"
      otherName=""
      className={clsx(styles.options, styles.genderOptions)}
      options={Object.values(Gender)
        .filter((gender) => gender !== 'other')
        .map((gender) => ({
          value: gender,
          label: t(`common.gender.${gender}`),
        }))}
    />
  );
}

const sortOptionsOrder = [
  SortByNameDirection.None,
  SortByNameDirection.Asc,
  SortByNameDirection.Desc,
];

function SortRadioGroup() {
  const { t } = useTranslation();

  const options = sortOptionsOrder.map((value) => ({
    value,
    label: t(`${translationBase}.sort.options.${value}`),
  }));

  return (
    <RadioGroup
      label={t(`${translationBase}.sort.title`)}
      readersOnlyLabel
      variant="dropdown"
      name="sort"
      className={styles.options}
      options={options}
    />
  );
}
